import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import FormCheckbox from '../../components/Forms/FormCheckbox';
// import Icon from '../../components/UI/Icon'; // Não está sendo usado
import { SystemMessageDto, PromptParameterDto } from '../../services/applications/types';
import { 
  getApplicationById,
  getPromptById,
  getPromptParameters,
  getUserStatuses,
  getPromptTools,
  updateApplicationPrompt 
} from '../../services/applications/applicationsService';

const PromptEdit: React.FC = () => {
  const { id, promptId } = useParams<{ id: string; promptId: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [applicationName, setApplicationName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [availableParameters, setAvailableParameters] = useState<PromptParameterDto[]>([]);
  const [selectedParameter, setSelectedParameter] = useState<string>('');
  
  // Form state
  const [prompt, setPrompt] = useState<SystemMessageDto>({
    message: '',
    system_message: '',
    user_status: '',
    application_id: id,
    is_active: true,
    temperature: 0.7,
    with_rag: false,
    tools: [],
    provider_name: 'ANTHROPIC', // Default provider
    model_name: 'claude-3-haiku-20240307'  // Default model
  });
  const [userStatuses, setUserStatuses] = useState<string[]>([]);
  const [availableTools, setAvailableTools] = useState<string[]>([]);
  const [selectedTool, setSelectedTool] = useState<string>('');

  // Function to insert a template parameter at cursor position
  const insertParameter = () => {
    if (!selectedParameter) return;
    
    const textArea = document.getElementById('message') as HTMLTextAreaElement;
    if (!textArea) return;
    
    const cursorPosition = textArea.selectionStart;
    const textBefore = textArea.value.substring(0, cursorPosition);
    const textAfter = textArea.value.substring(cursorPosition);
    
    const newMessage = `${textBefore}{${selectedParameter}}${textAfter}`;
    
    setPrompt(prev => ({
      ...prev,
      message: newMessage
    }));
    
    // Reset the selection
    setSelectedParameter('');
    
    // Focus back on textarea (delayed to ensure state update)
    setTimeout(() => {
      textArea.focus();
      textArea.selectionStart = cursorPosition + selectedParameter.length + 2; // +2 for the braces
      textArea.selectionEnd = cursorPosition + selectedParameter.length + 2;
    }, 50);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !promptId || !auth.user) return;
      
      setIsFetching(true);
      try {
        // Fetch application data
        const applicationData = await getApplicationById(auth.user, id);
        setApplicationName(applicationData.name || 'Application');

        // Fetch prompt data with individual error handling
        try {
          const promptData = await getPromptById(auth.user, id, promptId);
          console.log("Prompt data received:", promptData);
          setPrompt(promptData);
        } catch (promptErr) {
          console.error('Error fetching prompt data:', promptErr);
          setError('Could not fetch prompt details. This prompt may not exist or may be inaccessible.');
        }

        // Fetch parameters with individual error handling
        try {
          // Use a fallback for testing if API isn't ready yet
          const parametersData = await getPromptParameters(auth.user);
          setAvailableParameters(parametersData);
        } catch (paramsErr) {
          console.error('Error fetching parameters:', paramsErr);
          setError('Could not fetch parameter list');
        }
        
        // Fetch user statuses
        try {
          const statusesData = await getUserStatuses(auth.user);
          setUserStatuses(statusesData);
        } catch (statusErr) {
          console.error('Error fetching user statuses:', statusErr);
          // Fallback statuses will be provided by the component
        }
        
        // Fetch available tools
        try {
          const toolsData = await getPromptTools(auth.user);
          setAvailableTools(toolsData);
        } catch (toolsErr) {
          console.error('Error fetching prompt tools:', toolsErr);
          // Fallback tools will be provided by the component
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Could not fetch necessary data. Please try again later.');
      } finally {
        setIsFetching(false);
      }
    };
    
    fetchData();
  }, [id, promptId, auth.user]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!prompt.message || prompt.message.trim() === '') {
      errors.message = 'Message is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPrompt(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!auth.user) {
      setError('You must be authenticated to update a prompt');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (!id || !promptId) {
        throw new Error('Application ID and Prompt ID are required');
      }
      
      await updateApplicationPrompt(
        auth.user,
        id,
        promptId,
        prompt
      );
      
      // Navigate back to prompts list
      navigate(`/applications/${id}/prompts`);
    } catch (err: any) {
      console.error('Error updating prompt:', err);
      setError(err.message || 'Failed to update prompt');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={isFetching ? 'Loading...' : 'Edit System Message'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: applicationName || 'Application', to: `/applications/${id}` },
          { label: "System Messages", to: `/applications/${id}/prompts` },
          { label: isFetching ? 'Loading...' : 'Edit' }
        ]}
      />

      <Card>
        {isFetching ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Settings</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormSelect
                      label="User Status"
                      id="user_status"
                      name="user_status"
                      value={prompt.user_status || ''}
                      onChange={handleInputChange}
                      error={formErrors.user_status}
                      options={[
                        { value: "", label: "All Users (Default)" },
                        ...userStatuses.map(status => ({
                          value: status,
                          label: status
                        }))
                      ]}
                      leftIcon={<i className="fas fa-users text-gray-400"></i>}
                    />
                    
                    <div className="flex items-center h-full">
                      <FormCheckbox
                        id="is_active"
                        name="is_active"
                        label="Active"
                        checked={prompt.is_active === true}
                        onChange={(e) => 
                          setPrompt(prev => ({
                            ...prev,
                            is_active: e.target.checked
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Generation Parameters</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput
                      label="Temperature"
                      id="temperature"
                      name="temperature"
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={prompt.temperature?.toString() || "0.7"}
                      onChange={handleInputChange}
                      error={formErrors.temperature}
                      helperText="Controls response randomness (0-2)"
                      leftIcon={<i className="fas fa-thermometer-half text-gray-400"></i>}
                    />
                    
                    <div className="flex items-center h-full">
                      <FormCheckbox
                        id="with_rag"
                        name="with_rag"
                        label="Use RAG (Retrieval Augmented Generation)"
                        checked={prompt.with_rag === true}
                        onChange={(e) => 
                          setPrompt(prev => ({
                            ...prev,
                            with_rag: e.target.checked
                          }))
                        }
                        helperText="Enable additional knowledge retrieval from database"
                      />
                    </div>
                  </div>
                </div>
              </div>
            
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Available Tools</h3>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-md bg-gray-50 min-h-[50px]">
                    {prompt.tools?.map((tool, index) => (
                      <div key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center">
                        <span>{tool}</span>
                        <button 
                          type="button"
                          className="ml-1 text-blue-600 hover:text-blue-800"
                          onClick={() => {
                            setPrompt(prev => ({
                              ...prev,
                              tools: prev.tools?.filter((_, i) => i !== index)
                            }));
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                    {(!prompt.tools || prompt.tools.length === 0) && (
                      <span className="text-gray-400 text-sm">No tools selected</span>
                    )}
                  </div>
              
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="fas fa-tools text-gray-400"></i>
                      </div>
                      <select
                        value={selectedTool}
                        onChange={(e) => setSelectedTool(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a tool...</option>
                        {availableTools
                          .filter(tool => !prompt.tools?.includes(tool))
                          .map(tool => (
                            <option key={tool} value={tool}>
                              {tool}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        if (selectedTool) {
                          setPrompt(prev => ({
                            ...prev,
                            tools: [...(prev.tools || []), selectedTool]
                          }));
                          setSelectedTool('');
                        }
                      }}
                      disabled={!selectedTool}
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500">
                    Tools the assistant can use to perform tasks
                  </p>
                </div>
              </div>
            
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Available Parameters</h3>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-md bg-gray-50">
                    {availableParameters.length === 0 ? (
                      <p className="text-sm text-gray-500">Loading parameters...</p>
                    ) : (
                      availableParameters.map((param, index) => (
                        <div 
                          key={index} 
                          className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded text-xs cursor-pointer hover:bg-indigo-200"
                          onClick={() => {
                            setSelectedParameter(param.name);
                            insertParameter();
                          }}
                        >
                          {param.name}
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Click on a parameter to insert it in {'{parameter}'} format in the message.
                  </p>
                </div>
              </div>
            
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Message Content</h3>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Main Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={12}
                      value={prompt.message || prompt.system_message || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPrompt(prev => ({
                          ...prev,
                          message: value,
                          system_message: value  // Update both fields
                        }));

                        // Clear error when field is edited
                        if (formErrors.message) {
                          setFormErrors(prev => ({
                            ...prev,
                            message: ''
                          }));
                        }
                      }}
                      className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${
                        formErrors.message ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter the message content here. You can use variables in {variable} format."
                    />
                    {formErrors.message && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Use {'{variable}'} syntax for dynamic content. This message will be displayed to the user.
                    </p>
                  </div>
            
                  {/* System Media Content */}
                  {prompt.system_media !== undefined && (
                    <div>
                      <label
                        htmlFor="system_media"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Media Message (Optional)
                      </label>
                      <textarea
                        id="system_media"
                        name="system_media"
                        rows={6}
                        value={prompt.system_media || ''}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        placeholder="Enter specific instructions for media processing (optional)."
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Specific instructions for processing media content such as images and videos.
                      </p>
                    </div>
                  )}

                  {/* System Deferred Message Content */}
                  <div className="mt-4">
                    <label
                      htmlFor="system_deferred_message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Deferred Message (Optional)
                    </label>
                    <textarea
                      id="system_deferred_message"
                      name="system_deferred_message"
                      rows={6}
                      value={prompt.system_deferred_message || ''}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="Enter specific instructions for deferred messages (optional)."
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Specific instructions for when there are deferred messages in the system.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate(`/applications/${id}/prompts`)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update System Message'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default PromptEdit;